import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Pairing from '../CalendarActivities/Pairing';
import { DefaultButton } from '../Buttons'
import CircularLoading from '../CircularLoading'

import { reverseName } from './../../utilities/helpers';
import {
  formatMinutesToHoursWithMinutes,
  getFormattedPairingStartDateWithMonthNameAndYear,
  getFormattedPairingEndDateWithMonthNameAndYear
} from './../../utilities/calendarUtilities';
import {
  getBidWeightLabel,
  getBidMode,
  BidMode,
  sortPreferences
} from '../../scenes/BidSession/helpers';
import {RESERVE_DOWN_TO} from "../../data/constants";
import { useLanguage } from '../../context/LanguageContext';
import './styles.scss';

const OkButton = withStyles({
  root: {
    color: 'white',
    backgroundColor: '#009688',
    borderColor: '#009688',
    '&:hover': {
      backgroundColor: '#009688'
    }
  }
})(DefaultButton);

const CancelButton = withStyles({
  root: {
    color: 'white',
    backgroundColor: '#ADADAD',
    '&:hover': {
      backgroundColor: '#ADADAD'
    }
  }
})(DefaultButton);

const ReportDialog = ({
	openDialog,
	dialogMessage,
	dialogTitle,
  bidSessionId,
	okButtonLabel,
	handleOpen,
  fetchOtherOptions,
  fetchBidPreferences,

	user,
	bidName,
	bidSession,

  children
}) => {
  const { labels } = useLanguage();
  const REPORT = labels.REPORT || {};
  const commonLabels = labels.commonLabels || {};
  const RESERVE_DOWN = labels.RESERVE_DOWN || {};

  const [regularBidPreferences, setRegularBidPreferences] = useState([]);
  const [reservedPreferences, setReservedPreferences] = useState([]);
  const [otherOptions, setOtherOptions] = useState({});
  const [loading, setLoading] = useState(false);
	const printRef = useRef();

  useEffect(() => {
    const fetchData = async () => {
      if(openDialog) {
        setLoading(true);
        try {
          const preferencesResponse = await fetchBidPreferences(user?.staffId, bidSessionId);
          const sortedPreferences = sortPreferences(preferencesResponse.data?.entries || []);
          let regularBid = [];
          let reservedBid = [];
          sortedPreferences.forEach((preference) => {
            if(preference.preferenceOptionPropertyId === 0) {
              regularBid.push(preference);
            } else if(preference.preferenceOptionPropertyId === 15) {
              reservedBid.push(preference);
            }
          });
          setRegularBidPreferences(regularBid);
          setReservedPreferences(reservedBid);

          const otherOptionsResponse = await fetchOtherOptions(user?.staffId, bidSession?.currentBid?.bidSessionId, user?.currentCategory?.categoryId);
          setOtherOptions(otherOptionsResponse.data);
        } catch (error) {
          console.error('Error fetching report data:', error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, [openDialog, bidSessionId, user?.staffId, user?.currentCategory?.categoryId, bidSession?.currentBid?.bidSessionId])

  const handlePrint = () => {
    const printContent = printRef.current.innerHTML;
    const calendarWrapperEl = printRef.current?.querySelector('.Calendar-Wrapper-Report');
    const calendarDayEl = printRef.current?.querySelector('.CalendarDay');

    const calendarWrapperWidthPx = calendarWrapperEl
      ? Math.round(calendarWrapperEl.getBoundingClientRect().width)
      : 993;

    const calendarDayWidthPx = calendarDayEl
      ? calendarDayEl.getBoundingClientRect().width
      : 30.0909090909;

    const PRINT_STYLE_OVERRIDES = `
      @page { margin: 0; size: A4; }
      html, body { margin: 0; padding: 0; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      * { box-sizing: border-box; }
      @media print {
        .Calendar-Wrapper-Report,
        .Calendar-Table,
        .Calendar-Activities-Wrapper-Report,
        .Calendar-Activity-Row {
          width: ${calendarWrapperWidthPx}px !important;
          min-width: ${calendarWrapperWidthPx}px !important;
          max-width: ${calendarWrapperWidthPx}px !important;
        }
        .Calendar-Wrapper-Report { position: relative !important; }
        .Calendar-Activities-Wrapper-Report {
          position: absolute !important;
          left: 0 !important;
          top: 90px !important;
          margin: 0 !important;
          padding: 0 !important;
        }
        .Calendar-Activity-Row { position: absolute !important; left: 0 !important; margin-top: 0 !important; }
        .Calendar-Activity { position: absolute !important; }
        .CalendarDay {
          width: ${calendarDayWidthPx}px !important;
          min-width: ${calendarDayWidthPx}px !important;
          max-width: ${calendarDayWidthPx}px !important;
        }
      }
`;
    const printWindow = window.open('', '', `height=${600},width=${1000}`);
    printWindow.document.write(`
			<html>
				<head>
					<style>
						@page {}
						body {
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            .report {
              padding: 0;
            }

            .section {
              margin-bottom: 20px;
            }

            .reportTable{
              width: 100%;
              border-spacing: 0px;
            }

            .reportTh {
              background-color: #000000;
              color: white;
              padding: 10px 0;
            }

            .reportTd {
              padding: 10px;
            }

            .reportTbody {
              td {
                padding: 10px;
              }
              tr:nth-child(even) {
                background-color: #D3D3D3;
              }

              tr:nth-child(odd) {
                background-color: #ffffff;
              }
            }

            .pairingFooter{
              display: flex;
              justify-content: space-between;
            }

            .Calendar-Side {
              display: flex;
              flex-direction: column;
              width: 100px;
              font-size: 12px;
              text-align: right;
              padding: 123px 5px 0 5px;
            }

            .Calendar-Wrapper-Report {
              background: #fff;
              overflow-x: none !important;
              position: relative;
              &.sticked {
                position: fixed;
                top: 70px;
                z-index: 2;
                box-shadow: 0px 5px 5px grey;
              }
              .Calendar-Table {
                height: 100%;
                max-height: 220px;
                overflow: hidden;
                width: 100%;
                .CalendarDay {
                  border: 0.5px solid rgba(173,173,173,0.5);
                  display: inline-block;
                  height: 220px;
                  min-width: 15px;
                  vertical-align: top;
                  .click-wrapper {
                    cursor: pointer;
                    &.buffer {
                      cursor: auto;
                    }
                  }
                  .Number {
                    color: #EE5D2F;
                    margin-top: 25px;
                    font-size: 1rem;
                    font-weight: 700;
                    line-height: .9rem;
                    text-align: center;
                  }
                  .Day {
                    color: #9B9B9B;
                    font-weight: 300;
                    line-height: 1.1rem;
                    text-align: center;
                    font-size: 12px;
                  }
                  .Status {
                    display: block;
                    text-align: center;

                    > span {
                      border-radius: 50%;
                      content: '';
                      display: inline-block;
                      height: 10px;
                      width: 10px;
                    }
                  }
                }
                .WE {
                  background: #f7f7f7;
                }
                .CarryInOutDay {
                  background: #f7f7f7;
                  background-size: 6px 6px;
                  background-image: linear-gradient(45deg, transparent 45%, $ci-stripes 49%, $ci-stripes 51%, transparent 55%);

                }
              }
            }

            .Calendar-Activities-Wrapper-Report {
              display: block;
              position: absolute;
              top: 90px;
              width: 100%;
              .Calendar-Activity-Row {
                height: 122px;
                overflow: hidden;
                position: absolute;
                padding: 0 0 15px 0;
                width: 100%;
                margin-top: 23px;

                .Calendar-Activity {
                  position: absolute;
                  .Calendar-Activity-TypeUp,
                  .Calendar-Activity-TypeDown {
                    color: #4c4c4c;
                    font-size: 12px;
                    line-height: 16px;
                  }
                  .Calendar-Activity-TypeDown {
                    top: 95%;
                    display: flex;
                    flex-direction: column;
                  }
                  .Calendar-Activity-TypeUp {
                    bottom: 100%;
                    &.calendarLabel {
                      font-weight: bold;
                      &:before {
                        content: '*';
                      }
                    }
                  }
                  .Calendar-Activity-TypeUp.RightLabel {
                    text-align: right;
                    right: 0;
                    span {
                      display: inline-block;
                      max-width: 40px;
                      word-break: break-all;
                    }
                  }
                  .Calendar-Activity-Fill {
                    border-radius: 3px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
                    content: ' ';
                    display: inline-block;
                    height: 40px;
                    position: relative;
                    width: 100%;
                    z-index: 5;

                    .Calendar-Activity-Overlay {
                      height: 100%;
                      transition: 0.2s ease-in all;

                      &:hover {
                        background: rgba(255, 255, 255, 0.2);
                        cursor: pointer;
                      }
                    }
                  }
                  .Calendar-Activity-Extra-Vacation {
                    border-radius: 3px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
                    content: ' ';
                    display: inline-block;
                    height: 40px;
                    position: relative;
                    width: 100%;
                    z-index: 5;

                    .Calendar-Activity-Overlay {
                      height: 100%;
                      transition: 0.2s ease-in all;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      &:hover {
                        background: rgba(255, 199, 1, 0.342);
                        cursor: pointer;
                        content: ' ';
                        &::before {
                          content: '+';
                        }
                      }
                    }
                  }

                  .Calendar-Activity-Extra-Vacation-Active {
                    border-radius: 3px;
                    box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.2);
                    background-color: rgb(255, 199, 1) !important;

                    content: ' ';
                    display: inline-block;
                    height: 40px;
                    position: relative;
                    width: 100%;
                    z-index: 5;

                    .Calendar-Activity-Overlay {
                      height: 100%;
                      transition: 0.2s ease-in all;

                      &:hover {
                        cursor: pointer;
                        content: '';
                        background: rgba(233, 238, 171, 0.714);
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        &::before {
                          content: '-';
                        }
                      }
                    }
                  }
                }
                .vacation-slide {
                  border-radius: 3px;
                  cursor: pointer;
                  height: 41px;
                  margin-top: 16px;
                  opacity: 0.3;
                  position: absolute;
                  z-index: 2;
                }
              }
              .Calendar-Activity-Details {
                background: #f7f7f7;
                border: 1px solid #adadad;
                border-radius: 3px;
                left: 0;
                min-width: 500px;
                padding-bottom: 50px;
                position: absolute;
                top: 70px;
                .Calendar-Activity-Indicator {
                  display: inline-block;
                  position: absolute;
                  padding: 0;
                  width: 22px;
                  &:before {
                    content: '';
                    border-bottom: 22px solid #adadad;
                    border-left: 11px solid transparent;
                    border-right: 11px solid transparent;
                    position: absolute;
                    left: 0px;
                    bottom: 100%;
                  }
                  &:after {
                    content: '';
                    border-bottom: 20px solid #f7f7f7;
                    border-left: 10px solid transparent;
                    border-right: 10px solid transparent;
                    position: absolute;
                    left: 1px;
                    bottom: 100%;
                  }
                }
                h1 {
                  color: #3f51b5;
                  font-size: 24px;
                  line-height: 24px;
                  font-weight: normal;
                  margin: 10px 30px 10px 30px;
                }
                div {
                  background: white;
                  font-family: 'RobotoMono';
                  font-weight: 700;
                  padding: 28px 16px 36px 16px;
                }
                i {
                  color: #3f51b5;
                  font-size: 20px;
                  line-height: 20px;
                  position: absolute;
                  right: 15px;
                  top: 10px;
                  &:hover {
                    cursor: pointer;
                  }
                }
              }
            }

            .Pairing-Table-Report {
              font-weight: 400;
              .main-header {
                display: flex;
                justify-content: space-between;
                @media screen and(max-width: 1375px) {
                  margin: 5em 0 2em;
                }
                @media screen and(max-width: 1280px) {
                  margin: 2em 0 0 2em;
                }

                @media only screen and (min-width: 769px) and (max-width: 1024px) {
                  margin-top: 60px;
                }

                @media screen and (orientation: landscape) {
                  margin-top: 60px;
                }
              }
              .data-block {
                display: inline-block;
                padding: 0 20px 0 0;
                font-family: Roboto;
                .title {
                  color: #4a90e2;
                  text-transform: uppercase;
                  max-width: 50px;
                }
                .details {
                  font-weight: bold;
                }
              }
              p {
                margin: 0;
              }
              table {
                background: #fff;
                border-collapse: collapse;
                margin-top: 20px;
                text-align: center;
                width: 100%;
                .plane {
                  color: #ADADAD;
                  background-color: #ADADAD;
                  display: inline-block;
                  padding: 0;
                  width: 10px;
                  height: 10px;
                }
                .plane.active {
                  color: #3f51b5;
                  background-color: #3f51b5;
                }
                .plane::before {
                  content: '';
                  display: inline-block;
                  width: 10px;
                  height: 10px;
                  margin-right: 20px;
                }
                .layover-row {
                  background: #ffffff;
                  td {
                    border: none !important;
                    span {
                      background: #f6f6f6;
                      border-radius: 5px;
                      color: #607d8b;
                      display: block;
                      font-weight: bold;
                      margin: 0 auto;
                      padding: 0.5em 15%;
                      width: 80%;
                    }
                  }
                }
                td {
                  padding: 0.5em 0;
                  .station {
                    font-weight: bold;
                  }
                }
                thead {
                  th {
                    color: #3f51b5;
                    font-weight: bold;
                    text-align: center;
                    padding: 1em 1em 0 1em;
                  }
                }
              }
            }
					</style>
					<style>${PRINT_STYLE_OVERRIDES}</style>
					<title>${user.name} ${bidName} Report</title>

				</head>
				<body>
					${printContent}
					<footer>
						<p></p>
						<p>&copy; iFlight Rostering ${new Date().getFullYear()} All rights reserved.</p>
					</footer>
				</body>
			</html>
		`);
    printWindow.document.close();
    printWindow.print();

    handleOpen();
  };

  const renderWaivers = otherOptions?.entries?.find(
    option => option.value === true
  );

  // let block = 0, blockInPeriod = 0, credits = 0, creditsInPeriod = 0, timeAwayFromBaseInMin = 0;
  // bidSession?.pairings?.map(trip => {
  //   block += trip.pairing?.totalBlockTimeInMin || 0
  //   blockInPeriod += trip.pairing?.blockTimeInPeriodInMin || 0
  //   credits += trip.pairing?.totalCreditInMin || 0
  //   creditsInPeriod += trip.pairing?.creditInPeriodInMin || 0
  //   timeAwayFromBaseInMin += trip.pairing?.timeAwayFromBaseInMin || 0
  // })

  // TODO

  const tdStyle = 'center';
  const getRightScore = (score, optionLevel) => {
    if (optionLevel === RESERVE_DOWN_TO.minLevel) return ' ';
    if (optionLevel !== RESERVE_DOWN_TO.levelNonReserveDownTo) {
      if (optionLevel === RESERVE_DOWN_TO.levelAny)
        return `${RESERVE_DOWN.RDT} ${RESERVE_DOWN.ANY}`;
      else
        return `${RESERVE_DOWN.RDT} ${optionLevel - 1}`;
    } else if (bidSession?.bidMode === commonLabels.AUTOMATIC)
      return getBidWeightLabel(score);
    else return score;
  };

  const renderPreferences = (preferences, scope) => {
    const displayedGroups = new Set();
    return preferences?.map((preference, index) => {
      const isCombined = preference.groupName !== null;
      const groupKey = isCombined
        ? `${preference.scope}-${preference.score}-${preference.groupName}`
        : null;
      const showGroupHeader = isCombined && !displayedGroups.has(groupKey);
      if (showGroupHeader) displayedGroups.add(groupKey);

      return (
        <tr key={`${scope}_Preference_${index}`}>
          <td style={{ textAlign: tdStyle }}>
            {showGroupHeader || !isCombined ? scope : ''}
          </td>
          <td style={{ textAlign: tdStyle }}>
            {showGroupHeader || !isCombined
              ? getRightScore(preference.score, preference.optionLevel)
              : ''}
          </td>
          <td style={{ textAlign: tdStyle }}>
            {showGroupHeader
              ? REPORT.COMBINED
              : !isCombined
              ? REPORT.SINGLE
              : ''}
          </td>
          <td style={{ textAlign: tdStyle }}>
            {getRightScore(preference.score, preference.optionLevel)}
          </td>
          <td style={{ textAlign: tdStyle }}>
            {isCombined ? preference.groupSequence : ''}
          </td>
          <td style={{ textAlign: tdStyle }}>
            {preference.wish ? REPORT.DESIRE : REPORT.AVOID}
          </td>
          <td style={{ textAlign: tdStyle }}>{preference.description}</td>
        </tr>
      );
    });
  };

  const {unrankedRegularPreferences, rankedRegularPreferences} =
    regularBidPreferences.reduce(
      (accumulator, preference) => {
        if (preference.optionLevel === RESERVE_DOWN_TO.levelNonReserveDownTo && preference.score !== null) {
          accumulator.rankedRegularPreferences.push(preference);
        } else {
          accumulator.unrankedRegularPreferences.push(preference);
        }
        return accumulator;
      },
      {unrankedRegularPreferences: [], rankedRegularPreferences: []}
    );

  const reserveBidPreferencesDisplay = () =>
    renderPreferences(reservedPreferences, REPORT.RESERVE);

  return (
    <Dialog
      open={openDialog}
      onClose={handleOpen}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth={'lg'}
      fullWidth
      scroll='paper'
    >
      <DialogTitle id='alert-dialog-title'>{dialogTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText id='alert-dialog-description'>
          {dialogMessage}
        </DialogContentText>
        <div ref={printRef} style={{ width: '100%' }}>
          <h3>{`${user?.name} (${user?.staffId})`}</h3>
          <h3>{bidSession?.currentBid?.name}</h3>
          <div className='report'>
            {bidSession?.pairings?.length > 0 && (
              <div className='section'>
                <h4>{REPORT.AWARDED_LINE}:</h4>
                <p>{`${reverseName(bidSession?.user?.name)} - Emp ID: ${
                  bidSession?.user?.staffId
                } - Sen: ${bidSession?.user?.relativeSeniority}`}</p>
                <div style={{ display: 'flex', border: '1px solid #000000' }}>
                  <div className='Calendar-Side'>
                    {/* <span>seat</span> TODO */}
                    <span>{REPORT.LAYOVERS}</span>
                    <span>{REPORT.DEPARTURE}</span>
                    <span>{REPORT.ARRIVAL}</span>
                    <span>{REPORT.CREDIT_IN_PERIOD}</span>
                  </div>
                  {children}
                </div>
                {/* <p>{`Block: ${formatMinutesToHoursWithMinutes(block)} - Total block in period: ${formatMinutesToHoursWithMinutes(blockInPeriod)}`}</p>
                  <p>{`Credits: ${formatMinutesToHoursWithMinutes(credits)} - Total credits in period: ${formatMinutesToHoursWithMinutes(creditsInPeriod)}`}</p>
                  <p>{`Time away from base: ${formatMinutesToHoursWithMinutes(timeAwayFromBaseInMin)}`}</p>           TODO      */}
              </div>
            )}
            {bidSession?.pairings?.length > 0 && (
              <div className='section'>
                <h4>{REPORT.PAIRING_DETAILS}</h4>
                {bidSession?.pairings?.map(
                  (trip, index) =>
                    trip.pairing && (
                      <div key={`pairing_div_${index}`} style={{ borderBottom: '1px solid #000000' }}>
                        <h3>{trip.activityName}</h3>
                        <Pairing
                          pairing={trip.pairing}
                          name={trip.activityName}
                          days={trip.days}
                          startDate={trip.startInterval}
                          endDate={trip.endInterval}
                          attributes={trip.groupActivitiesType}
                          reportMode
                        />
                      </div>
                    )
                )}
              </div>
            )}
            {bidSession?.layoverHotelInformation?.length > 0 && (
              <div className='section'>
                <h4>{REPORT.HOTEL_INFORMATION}</h4>
                <table className='reportTable'>
                  <thead>
                    <tr>
                      <th className='reportTh'>{REPORT.DATE}</th>
                      <th className='reportTh'>{REPORT.STATION}</th>
                      <th className='reportTh'>{REPORT.DURATION}</th>
                      <th className='reportTh'>{REPORT.HOTEL_DETAILS}</th>
                    </tr>
                  </thead>
                  <tbody className='reportTbody'>
                    {bidSession?.layoverHotelInformation?.map(
                      (hotelInfo, index) => {
                        return (
                          <tr key={`layoverHotelInformation_${index}`}>
                            <td>
                              {getFormattedPairingStartDateWithMonthNameAndYear(
                                hotelInfo.relativeStartFromStation
                              )}
                              <br />
                              {getFormattedPairingEndDateWithMonthNameAndYear(
                                hotelInfo.relativeStartFromStation,
                                hotelInfo.layOverTimeInMin
                              )}
                            </td>
                            <td style={{ textAlign: tdStyle }}>
                              {hotelInfo.fromStationId}
                            </td>
                            <td style={{ textAlign: tdStyle }}>
                              {formatMinutesToHoursWithMinutes(
                                hotelInfo.layOverTimeInMin
                              )}
                            </td>
                            <td>
                              {hotelInfo.hotelName ? (
                                <>
                                  {hotelInfo.hotelName}
                                  <br />
                                  {hotelInfo.hotelAddress}
                                  <br />
                                  {hotelInfo.hotelPhone}
                                </>
                              ) : (
                                <>{REPORT.HOTEL_INFORMATION_UNAVAILABLE}</>
                              )}
                            </td>
                          </tr>
                        );
                      }
                    )}
                  </tbody>
                </table>
              </div>
            )}
            {(regularBidPreferences?.length > 0 ||
              reservedPreferences?.length > 0) && (
              <div className='section'>
                <h4>{REPORT.PREFERENCES_AND_WAIVERS}</h4>
                <table className='reportTable'>
                  <thead>
                    <tr>
                      <th className='reportTh'>{REPORT.SCOPE}</th>
                      <th className='reportTh'>{REPORT.IMPORTANCE}</th>
                      <th className='reportTh'>{REPORT.TYPE}</th>
                      <th className='reportTh'>{REPORT.PREF_NUMBER}</th>
                      <th className='reportTh'>{REPORT.GROUP_NUMBER}</th>
                      <th className='reportTh'>{REPORT.DESIRE_AVOID}</th>
                      <th className='reportTh'>{REPORT.REQUESTS}</th>
                    </tr>
                  </thead>
                  <tbody className='reportTbody'>
                  {regularBidPreferences?.length > 0 && (
                    <>
                      <h3>{REPORT.REGULAR}</h3>
                      {rankedRegularPreferences?.length > 0 && unrankedRegularPreferences?.length > 0 && (
                        <>
                          <tr>
                            <th colSpan="7" className="section-title">{REPORT.UNRANKED} {REPORT.PREFERENCES}</th>
                          </tr>
                          {renderPreferences(unrankedRegularPreferences, REPORT.REGULAR)}
                          <tr>
                            <th colSpan="7" className="section-title">{REPORT.RANKED} {REPORT.PREFERENCES}</th>
                          </tr>
                          {renderPreferences(rankedRegularPreferences, REPORT.REGULAR)}
                        </>
                      )}
                      {rankedRegularPreferences?.length > 0 && unrankedRegularPreferences?.length === 0 &&
                        renderPreferences(rankedRegularPreferences, REPORT.REGULAR)
                      }
                      {unrankedRegularPreferences?.length > 0 && rankedRegularPreferences?.length === 0 &&
                        renderPreferences(unrankedRegularPreferences, REPORT.REGULAR)
                      }
                    </>
                  )}

                  {reservedPreferences?.length > 0 && (
                    <>
                      <h3>{REPORT.RESERVE}</h3>
                      {reserveBidPreferencesDisplay()}
                    </>
                  )}
                  </tbody>
                </table>
              </div>
            )}
            {renderWaivers && (
              <div className='section'>
                <table className='reportTable'>
                  <thead>
                    <tr>
                      <th className='reportTh'>{REPORT.WAIVERS}</th>
                      <th className='reportTh'>{REPORT.STATUS}</th>
                    </tr>
                  </thead>
                  <tbody className='reportTbody'>
                    {otherOptions?.entries
                      ?.filter(option => option.value)
                      .map((option, index) => {
                        if (option.value) {
                          return (
                            <tr key={`otherOptions_${index}`}>
                              <td>{option.name}</td>
                              <td style={{ textAlign: 'center' }}>
                                {REPORT.SELECTED}
                              </td>
                            </tr>
                          );
                        }
                      })}
                  </tbody>
                </table>
              </div>
            )}
            {bidSession?.scores?.entries?.length > 0 && (
              <div className='section'>
                <h4>Score</h4>
                <table className='reportTable'>
                  <thead>
                    <tr>
                      <th className='reportTh'>
                        {getBidMode() === BidMode.PRIORITY.biddingMode
                          ? REPORT.PRIORITY
                          : REPORT.WEIGHT}
                      </th>
                      <th className='reportTh'>{REPORT.DESCRIPTION}</th>
                      <th className='reportTh'>{REPORT.OCCURENCE}</th>
                      {getBidMode() !== BidMode.PRIORITY.biddingMode && (
                        <th className='reportTh'>{REPORT.SCORE}</th>
                      )}
                    </tr>
                  </thead>
                  <tbody className='reportTbody'>
                    {bidSession?.scores?.entries?.map((score, index) => {
                      return (
                        <tr
                          key={`score_${index}`}
                          style={
                            score.wish === 'Award' ? { color: '#479d75' } : {}
                          }
                        >
                          <td style={{ textAlign: tdStyle }}>{score.weight}</td>
                          <td style={{ textAlign: tdStyle }}>{`${
                            score.wish === 'Award'
                              ? REPORT.DESIRE
                              : REPORT.AVOID
                          } ${score.description}`}</td>
                          <td style={{ textAlign: tdStyle }}>
                            {score.occurrence}
                          </td>
                          {getBidMode() !== BidMode.PRIORITY.biddingMode && (
                            <td style={{ textAlign: tdStyle }}>
                              {score.score}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
      <DialogActions>
        <CancelButton onClick={handleOpen} color='primary'>
          {labels.COMBINED_PREFERENCES?.CANCEL || labels.openTimeSearchLabels?.CANCEL || 'Cancel'}
        </CancelButton>
        <OkButton onClick={() => handlePrint()} color='primary'>
          {labels.COMBINED_PREFERENCES?.ADD || okButtonLabel}
        </OkButton>
      </DialogActions>
    </Dialog>
  );
};

ReportDialog.propTypes = {
  openDialog: PropTypes.bool.isRequired,
  dialogMessage: PropTypes.string.isRequired,
  dialogTitle: PropTypes.string.isRequired,
  handleOpen: PropTypes.func,
  report: PropTypes.object,
  preferences: PropTypes.array,
  fetchOtherOptions: PropTypes.func.isRequired
};

ReportDialog.defaultProps = {
  openDialog: false,
  dialogMessage: '',
  dialogTitle: '!',
  okButtonLabel: 'Ok',
  handleOpen: () => {},
  report: {},
  preferences: [],
  fetchOtherOptions: () => {}
};

const mapStateToProps = state => ({
  ...state.bidSession
});

export default connect(mapStateToProps)(ReportDialog);
